import React from 'react';
import Card from '../../../../components/Card';
import CardHeader from '../../../../components/CardHeader';
import styled from 'styled-components';
import Select from '../../../../components/Select/SelectFilter';
import BarGraph from '../../../../components/Charts/BarGraph';
import moment from 'moment';
import ExportButton from '../../../../components/Export';
import get from 'lodash/get';
import { I18n } from 'react-redux-i18n';

const PreparationBarGraphContainer = styled.div`
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  .exportButtonContainer {
    padding-bottom: 10px;
    margin-top: 10px;
  }

  .filterContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 104px;
    background-color: rgba(173, 181, 189, 0.05);
    margin-bottom: 52px;
    padding-left: 20px;
  }

  .filterContainer:not(:last-children) {
    margin-right: 45px;
  }

  .calendarContainer {
    width: 300px;
    margin-right: 45px;
  }

  .filterCard {
    margin: 0px;
    margin-bottom: 20px;
  }
`;

const TooltipContainer = styled.div`
  min-width: 300px;
  padding: 15px;
  display: flex;
  flex-direction: column;

  .rowTooltip {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
  }

  .rowTooltipLabel {
    font-family: ${(props) => props.theme.fonts.sfcompact};
    font-size: 12px;
    color: ${(props) => props.theme.colors.dark};
  }

  .rowTooltipLabel {
    text-transform: uppercase;
  }

  .rowTooltipValue {
    margin-left: auto;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-weight: 600;
  }
`;

class PreparationBarGraph extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedWine: null,
      selectedVintages: [],
      renderLabelsPositions: {}
    };
  }

  componentDidUpdate(prevProps) {
    if (
      (prevProps.data != this.props.data ||
        prevProps.client_wines != this.props.client_wines) &&
      this.props.data &&
      this.props.data.length > 0 &&
      this.props.client_wines &&
      this.props.client_wines.length > 0
    ) {
      const selectedWine = get(this.props.client_wines, '[0].wine');
      const defaultVintages = [...this.props.data]
        .filter((item) => item.name === selectedWine && item.vintage)
        .map((item) => item.vintage);
      defaultVintages.sort((a, b) => Number(b) - Number(a));
      this.setState({
        selectedWine: {
          value: selectedWine,
          label: selectedWine
        },
        selectedVintages: defaultVintages
          .slice(0, 5)
          .map((item) => ({ value: item, label: item }))
      });
    }
  }

  getOptions() {
    const { data } = this.props;
    const wineOptions = [...new Set(data.map((item) => item.name))].map(
      (item) => ({
        value: item,
        label: item
      })
    );
    const vintageOptions = [
      ...new Set(
        data
          .filter(
            (item) =>
              item &&
              !isNaN(item.vintage) &&
              (item.avg_price || item.avg_web_price)
          )
          .map((item) => item.vintage)
      )
    ]
      .sort()
      .map((item) => ({ value: item, label: item }));
    return { wine: wineOptions, vintage: vintageOptions };
  }

  handleMultiSelectChange(selectedOptions) {
    if (selectedOptions.length > 6) {
      selectedOptions.shift();
    }
    this.setState({ selectedVintages: selectedOptions });
  }

  filterData() {
    const { data } = this.props;
    let { selectedWine, selectedVintages } = this.state;

    if (!selectedVintages || !selectedVintages.length || !data.length) {
      return [];
    }
    let filteredData = [...data];
    filteredData = selectedWine
      ? filteredData.filter((item) => item.name === selectedWine.label)
      : [];

    selectedVintages = selectedVintages.map((item) => item.label);
    filteredData = filteredData.filter((item) =>
      selectedVintages.includes(item.vintage)
    );
    const setPrimeur = [];
    const setActual = [];
    filteredData.sort(function (a, b) {
      return String(a.vintage).localeCompare(String(b.vintage));
    });
    filteredData.forEach((wine) => {
      setPrimeur.push({
        name: wine.vintage,
        value: wine.avg_price ? wine.avg_price : 0
      });
      setActual.push({
        name: wine.vintage,
        value: wine.avg_web_price ? wine.avg_web_price : 0
      });
    });
    if (selectedVintages.length > 1) {
      setPrimeur.unshift(this.mergeSetValues(setPrimeur));
      setActual.unshift(this.mergeSetValues(setActual));
    }
    return [setPrimeur, setActual];
  }

  mergeSetValues(set) {
    const mergedSet = {};

    mergedSet.name = set.map((item) => item.name).join(' - ');
    set = set.filter((item) => item && item.value);
    mergedSet.value =
      set.map((item) => item.value).reduce((p, c) => p + c, 0) / set.length;
    return mergedSet;
  }

  getMaxDate() {
    const { data } = this.props;
    let dates = data
      .filter((item) => item.last_update)
      .map((item) => moment(item.last_update, 'YYYY-MM-DD'));
    return moment.max(dates);
  }

  renderTooltip(data, labels, colors) {
    let dataTooltip = [...data];
    return (
      <TooltipContainer colors={colors}>
        {dataTooltip.map(
          (set, index) =>
            set &&
            set.value &&
            set.value > 0 && (
              <div key={index} className="rowTooltip">
                <p className="rowTooltipLabel">
                  {`${labels[index]} ${set.name ? set.name : ''}`}
                </p>
                <p className="rowTooltipValue">{set.value.toFixed(2)}</p>
              </div>
            )
        )}
      </TooltipContainer>
    );
  }

  render() {
    const { selectedWine, selectedVintages } = this.state;
    const filteredData = this.filterData();
    const options = this.getOptions();
    const maxDate = this.getMaxDate();

    return (
      <PreparationBarGraphContainer id="compare-graph">
        <Card>
          <CardHeader
            title={I18n.t('primeur.compare.barchart.title')}
            fontSize="20px"
            iconRight
            rightComponents={
              <ExportButton
                jpg
                pdf
                xls
                path="/api/v1/clients/me/primeur/avg_price/xls/"
                options={{
                  wine: selectedWine ? selectedWine.label : ''
                }}
                name={I18n.t('primeur.compare.barchart.title')}
                targetElement="#compare-graph"
              />
            }
          />
          <div className="filterContainer ignored-export">
            <Select
              selectedOption={selectedWine}
              onChange={(selectedOption) =>
                this.setState({ selectedWine: selectedOption })
              }
              options={options.wine}
              label={I18n.t('primeur.compare.barchart.filters.wine')}
              containerStyles={`width:240px;  :not(:last-child){margin-right:30px}`}
            />
            <Select
              isMulti
              defaultValue={[{ value: 2018, label: '2018' }]}
              value={selectedVintages}
              onChange={(selectedOption) =>
                this.handleMultiSelectChange(selectedOption)
              }
              minWidth="400px"
              options={options.vintage.sort().reverse()}
              containerStyles={`width:240px; :not(:last-child){margin-right:30px}`}
              label={I18n.t('primeur.compare.barchart.filters.vintage')}
            />
          </div>
          <BarGraph
            data={filteredData}
            renderTooltip={(props) =>
              this.renderTooltip(
                props,
                [
                  I18n.t('primeur.compare.barchart.primeurPriceLegend'),
                  I18n.t('primeur.compare.barchart.currentPriceLegend') +
                    maxDate.format('DD/MM/YYYY')
                ],
                ['#2B3D54', '#e25d34']
              )
            }
            height={500}
            tooltipOffset={{ x: 25, y: 70 }}
            yAxisProps={{
              width: 40,
              domain: [0, (dataMax) => (dataMax + dataMax / 5).toFixed(0)]
            }}
            labels={[
              I18n.t('primeur.compare.barchart.primeurPriceLegend'),
              I18n.t('primeur.compare.barchart.currentPriceLegend') +
                maxDate.format('DD/MM/YYYY')
            ]}
            colors={['#2B3D54', '#e25d34']}
          />
        </Card>
      </PreparationBarGraphContainer>
    );
  }
}

export default PreparationBarGraph;
